import type { NetWorthPeriodItem } from '@/api/types';

type ProjectionItem = NetWorthPeriodItem & { isProjected?: boolean };

// Above this many projected points the yearly chart gets too dense on mobile,
// so we render every Nth projected year while always keeping the first/last
// points and any callout (funds-depleted / FI-reached) years.
export const PROJECTION_DECIMATION_THRESHOLD = 25;

/**
 * Reduce a long projected span to a readable subset for the chart.
 * Historical points are untouched. Within the projected portion we keep the
 * first and last points, any callout years (funds-depleted / FI-reached), and
 * every Nth remaining point. Below the threshold nothing is dropped.
 */
export function decimateProjectedItems(
    items: ProjectionItem[],
    calloutYears: Set<number>,
    threshold: number = PROJECTION_DECIMATION_THRESHOLD
): ProjectionItem[] {
    const projectedItems = items.filter((item) => item.isProjected);
    if (projectedItems.length <= threshold) {
        return items;
    }

    const stride = Math.max(1, Math.ceil(projectedItems.length / threshold));
    const lastProjectedLabel = projectedItems[projectedItems.length - 1]?.periodLabel;
    let projectedIndex = -1;

    return items.filter((item) => {
        if (!item.isProjected) return true;
        projectedIndex += 1;
        const year = Number.parseInt(item.periodLabel, 10);
        const isEndpoint = projectedIndex === 0 || item.periodLabel === lastProjectedLabel;
        const isCallout = Number.isFinite(year) && calloutYears.has(year);
        return isEndpoint || isCallout || projectedIndex % stride === 0;
    });
}
